import { useState, useEffect } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, FAINT, LINE, SURFACE, TEXT, NEUTRAL_BG, text, btn, btnSm, linkBtn, iconBtn, segWrap, segBtn, errorBanner, SHADOW_MODAL, SHADOW_POP, SCRIM, WHITE, ROW_LINE, INK } from "./constants";
import { CloseButton, BackButton } from "./shared";
import { X, Check, CircleAlert } from "lucide-react";
import { track } from "./lib/analytics";
import { useIsMobile } from "./lib/responsive";
import Logo from "./Logo";
import { PageLoader } from "./LogoLoader";

const ADMIN_EMAILS = ["borja@rutment.com", "borjaafanderibera@gmail.com"];

const PRIVACY_POLICY = `PRIVACY AND DATA PROTECTION POLICY

Data Controller
RUTMENT TECH S.L.U. (in constitution), registered address at Calle Serrano Anguita 7, 5th floor, 28004 Madrid, Spain. Contact email: borja@rutment.com.

Purpose of Processing
Personal data provided through this form will be processed exclusively for the following purposes:
• To evaluate your candidacy in the selection process for which you have been contacted.
• To manage communication between the candidate and the Rutment team and/or the client company throughout the process.
• To create a professional profile based on your responses, which will be shared confidentially with the client company.

Legal Basis
The processing of your data is based on the explicit consent you provide by accepting this policy, in accordance with Article 6.1(a) of the General Data Protection Regulation (GDPR).

Data Processed
We will process the data you provide directly in this form (name, city, salary, notice period and questionnaire responses), as well as professional information publicly available on LinkedIn, including: previous and current positions, education, profile information (including profile photo), publications and professional contact information. This information may be consulted, stored and used as part of the evaluation of your candidacy.

Recipients
Your data may be shared with the client company for which the selection process is being conducted, under a confidentiality agreement. We will not share your data with third parties for other purposes without your explicit consent.

Data Retention
We will retain your data for as long as necessary to manage the active selection process and, thereafter, in our candidate database for potential future opportunities that may match your profile. You may withdraw your consent and request the complete deletion of your data at any time by contacting us. We will process deletion requests within 30 business days.

Your Rights
Under the GDPR, you have the right to:
• Access your personal data.
• Rectify inaccurate or incomplete data.
• Request the deletion of your data (right to be forgotten).
• Object to processing or request its restriction.
• Request the portability of your data.

To exercise any of these rights, please send an email to borja@rutment.com with the subject line "GDPR Rights Request" and a copy of your identity document. We will respond within a maximum of 30 business days.

Security
Rutment applies appropriate technical and organisational measures to ensure the security of your data and prevent unauthorised access, loss or destruction.`;

const PRIVACY_POLICY_ES = `POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS

Responsable del tratamiento
RUTMENT TECH S.L.U. (en constitución), con domicilio en Calle Serrano Anguita 7, 5.ª planta, 28004 Madrid, España. Email de contacto: borja@rutment.com.

Finalidad del tratamiento
Los datos personales que facilites en este formulario se tratarán exclusivamente para las siguientes finalidades:
• Evaluar tu candidatura en el proceso de selección para el que se te ha contactado.
• Gestionar la comunicación entre el candidato y el equipo de Rutment y/o la empresa cliente durante el proceso.
• Elaborar un perfil profesional a partir de tus respuestas, que se compartirá de forma confidencial con la empresa cliente.

Base legal
El tratamiento de tus datos se basa en el consentimiento explícito que otorgas al aceptar esta política, conforme al artículo 6.1.a) del Reglamento General de Protección de Datos (RGPD).

Datos tratados
Trataremos los datos que facilites directamente en este formulario (nombre, ciudad, salario, preaviso y respuestas al cuestionario), así como la información profesional disponible públicamente en LinkedIn, incluidos: puestos anteriores y actuales, formación, información del perfil (incluida la foto de perfil), publicaciones y datos de contacto profesional. Esta información podrá consultarse, almacenarse y utilizarse como parte de la evaluación de tu candidatura.

Destinatarios
Tus datos podrán compartirse con la empresa cliente para la que se realiza el proceso de selección, bajo un acuerdo de confidencialidad. No cederemos tus datos a terceros con otros fines sin tu consentimiento explícito.

Conservación de los datos
Conservaremos tus datos durante el tiempo necesario para gestionar el proceso de selección activo y, después, en nuestra base de datos de candidatos para posibles oportunidades futuras que encajen con tu perfil. Puedes retirar tu consentimiento y solicitar la eliminación completa de tus datos en cualquier momento contactándonos. Atenderemos las solicitudes de eliminación en un plazo de 30 días hábiles.

Tus derechos
Conforme al RGPD, tienes derecho a:
• Acceder a tus datos personales.
• Rectificar los datos inexactos o incompletos.
• Solicitar la supresión de tus datos (derecho al olvido).
• Oponerte al tratamiento o solicitar su limitación.
• Solicitar la portabilidad de tus datos.

Para ejercer cualquiera de estos derechos, envía un email a borja@rutment.com con el asunto "Solicitud de derechos RGPD" y una copia de tu documento de identidad. Responderemos en un plazo máximo de 30 días hábiles.

Seguridad
Rutment aplica las medidas técnicas y organizativas adecuadas para garantizar la seguridad de tus datos y evitar su acceso no autorizado, pérdida o destrucción.`;

// Proceso por defecto cuando la posición no tiene uno propio. Es texto de
// interfaz, no dato guardado: se pinta en el idioma elegido.
const defaultProcess = (lang) => lang === "es" ? [
  { n: 1, label: "Candidatura y formulario de cualificación", desc: "Completa este formulario para que podamos revisar tu perfil.", interviewers: [], address: null },
  { n: 2, label: "Entrevistas con el cliente (online)", desc: "Ronda de entrevistas online con el equipo de contratación de la empresa.", interviewers: [], address: null },
  { n: 3, label: "Ronda final (presencial)", desc: "Entrevistas presenciales.", interviewers: [], address: null },
  { n: 4, label: "Seguimiento del proceso", desc: "Regístrate en {RUTMENT_CANDIDATES_LINK} y sigue el estado del proceso.", interviewers: [], address: null },
] : [
  { n: 1, label: "Application & qualification form", desc: "Please, complete this form so we can review your profile.", interviewers: [], address: null },
  { n: 2, label: "Client interviews (online)", desc: "Round of interviews with the company's hiring team, conducted online.", interviewers: [], address: null },
  { n: 3, label: "Final round (in person)", desc: "In person interviews.", interviewers: [], address: null },
  { n: 4, label: "Process tracking", desc: "Sign up in {RUTMENT_CANDIDATES_LINK} and follow the process status.", interviewers: [], address: null },
];

// Mensajes de error por código: se traducen al pintar
const ERROR_TEXT = {
  invalid: { es: "Este enlace no es válido. Contacta con tu recruiter.", en: "This link is not valid. Please contact your recruiter." },
  "not-found": { es: "No se ha encontrado la posición.", en: "Position not found." },
  generic: { es: "Se ha producido un error. Inténtalo de nuevo.", en: "An error occurred. Please try again." },
};

// ───────── Markdown helpers ─────────

const stripJdHeader = (text) => {
  if (!text) return text;
  return text
    .replace(/^\s*\*\*Job\s*Description\*\*\s*:?\s*\n*/i, "")
    .replace(/^\s*#{1,3}\s*Job\s*Description\s*\n*/i, "")
    .trimStart();
};

const renderInlineBold = (text) => {
  if (text == null) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} style={{ color: BRAND, fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed === "") return <div key={idx} style={{ height: 8 }} />;

    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      return (
        <div key={idx} style={{ color: BRAND, fontWeight: 700, fontSize: 13, margin: "14px 0 6px" }}>
          {renderInlineBold(headerMatch[2])}
        </div>
      );
    }

    const fullBold = trimmed.match(/^\*\*([^*]+)\*\*$/);
    if (fullBold) {
      return (
        <div key={idx} style={{ color: BRAND, fontWeight: 700, fontSize: 13, margin: "14px 0 6px" }}>
          {fullBold[1]}
        </div>
      );
    }

    return <div key={idx}>{renderInlineBold(line)}</div>;
  });
};

// Renders step description, replacing {RUTMENT_CANDIDATES_LINK} with an anchor to the candidates portal
const renderStepDesc = (desc, lang) => {
  if (!desc) return null;
  if (!desc.includes("{RUTMENT_CANDIDATES_LINK}")) return desc;
  const parts = desc.split("{RUTMENT_CANDIDATES_LINK}");
  return parts.map((p, i) => (
    <span key={i}>
      {p}
      {i < parts.length - 1 && (
        <a href="https://candidates.rutment.com" target="_blank" rel="noopener noreferrer"
          style={{ color: BRAND, fontWeight: 700, textDecoration: "underline" }}>
          {lang === "es" ? "el portal de candidatos de Rutment" : "Rutment candidates"}
        </a>
      )}
    </span>
  ));
};

// ───────── Selection Process Editor (admin only) ─────────

const SelectionProcessEditor = ({ steps, positionId, onSave, onClose, lang }) => {
  // Avisos en la página, junto al botón (24/10): antes eran alert() del navegador
  const [formErr, setFormErr] = useState("");
  const [editedSteps, setEditedSteps] = useState(JSON.parse(JSON.stringify(steps)));
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const isMobile = useIsMobile();

  const updateStep = (idx, field, value) => {
    setEditedSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addInterviewer = (idx) => {
    setEditedSteps(prev => prev.map((s, i) =>
      i === idx ? { ...s, interviewers: [...(s.interviewers || []), { name: "", role: "", photo_url: "" }] } : s
    ));
  };

  const updateInterviewer = (stepIdx, intIdx, field, value) => {
    setEditedSteps(prev => prev.map((s, i) => {
      if (i !== stepIdx) return s;
      return { ...s, interviewers: s.interviewers.map((iv, j) => j === intIdx ? { ...iv, [field]: value } : iv) };
    }));
  };

  const removeInterviewer = (stepIdx, intIdx) => {
    setEditedSteps(prev => prev.map((s, i) => {
      if (i !== stepIdx) return s;
      return { ...s, interviewers: s.interviewers.filter((_, j) => j !== intIdx) };
    }));
  };

  const uploadPhoto = async (file, stepIdx, intIdx) => {
    const key = `${stepIdx}-${intIdx}`;
    setUploadingIdx(key);
    try {
      const ext = file.name.split(".").pop();
      const path = `interviewer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("candidate-photos").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("candidate-photos").getPublicUrl(path);
      updateInterviewer(stepIdx, intIdx, "photo_url", publicUrl);
    } catch (e) {
      setFormErr((lang === "es" ? "Error al subir la foto: " : "Photo upload failed: ") + (e.message || e));
    }
    setUploadingIdx(null);
  };

  const handleSave = async () => {
    setFormErr("");
    setSaving(true);
    try {
      const { error } = await supabase.from("positions").update({ selection_process: editedSteps }).eq("id", positionId);
      if (error) throw error;
      onSave(editedSteps);
    } catch (e) {
      setFormErr((lang === "es" ? "Error al guardar: " : "Save failed: ") + (e.message || e));
    }
    setSaving(false);
  };

  const inp = { width: "100%", padding: isMobile ? "11px 12px" : "8px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none", ...(isMobile ? { minHeight: 44 } : {}) };
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, color: TEXT, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 };

  // En móvil, hoja a pantalla completa con su cabecera y su propio scroll
  const overlayStyle = isMobile
    ? { position: "fixed", inset: 0, background: WHITE, zIndex: 1000, display: "flex", flexDirection: "column" }
    : { position: "fixed", inset: 0, background: SCRIM, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
  const panelStyle = isMobile
    ? { background: WHITE, padding: "0 16px", paddingBottom: "calc(16px + env(safe-area-inset-bottom))", width: "100%", flex: 1, overflowY: "auto" }
    : { background: WHITE, borderRadius: 16, padding: "28px 32px", maxWidth: 760, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: SHADOW_MODAL };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 16 : 24, ...(isMobile ? { position: "sticky", top: 0, background: WHITE, zIndex: 1, padding: "8px 0", borderBottom: `1px solid ${ROW_LINE}` } : {}) }}>
          <h2 style={{ ...text.cardTitle, margin: 0 }}>{lang === "es" ? "Editar proceso de selección" : "Edit selection process"}</h2>
          <CloseButton onClick={onClose} label={lang === "es" ? "Cerrar" : "Close"} />
        </div>

        {editedSteps.map((step, stepIdx) => (
          <div key={stepIdx} style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 12, padding: isMobile ? 14 : 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: BRAND_LIGHT, color: BRAND, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>{step.n}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>{lang === "es" ? "Paso" : "Step"} {step.n}</div>
            </div>

            <label style={lbl}>{lang === "es" ? "Nombre del paso" : "Step name"}</label>
            <input value={step.label} onChange={e => updateStep(stepIdx, "label", e.target.value)} style={{ ...inp, marginBottom: 10 }} />

            <label style={lbl}>{lang === "es" ? "Descripción" : "Description"}</label>
            <textarea value={step.desc} onChange={e => updateStep(stepIdx, "desc", e.target.value)} rows={2} style={{ ...inp, marginBottom: 10, resize: "vertical" }} />

            <label style={lbl}>{lang === "es" ? "Dirección (opcional)" : "Address (optional)"}</label>
            <input value={step.address || ""} onChange={e => updateStep(stepIdx, "address", e.target.value || null)} placeholder={lang === "es" ? "p. ej. Calle Serrano 1, 28006 Madrid" : "e.g. Calle Serrano 1, 28006 Madrid"} style={{ ...inp, marginBottom: 14 }} />

            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>{lang === "es" ? "Entrevistadores" : "Interviewers"}</div>
            {(step.interviewers || []).map((iv, intIdx) => {
              const key = `${stepIdx}-${intIdx}`;
              return (
                <div key={intIdx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, padding: 10, background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 8, ...(isMobile ? { flexWrap: "wrap" } : {}) }}>
                  <label style={{ cursor: "pointer", flexShrink: 0, position: "relative" }}>
                    {iv.photo_url ? (
                      <img src={iv.photo_url} alt={iv.name || "interviewer"} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", display: "block" }} />
                    ) : (
                      <div style={{ width: 44, height: 44, borderRadius: "50%", background: NEUTRAL_BG, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: 11, fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>{lang === "es" ? "+ foto" : "+ photo"}</div>
                    )}
                    {uploadingIdx === key && (
                      <div style={{ position: "absolute", inset: 0, background: SCRIM, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontSize: 11 }}>...</div>
                    )}
                    <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0], stepIdx, intIdx)} style={{ display: "none" }} />
                  </label>
                  <input value={iv.name} onChange={e => updateInterviewer(stepIdx, intIdx, "name", e.target.value)} placeholder={lang === "es" ? "Nombre" : "Name"} style={isMobile ? { ...inp, flex: "1 1 140px" } : { ...inp, flex: 1, padding: "7px 10px", fontSize: 12 }} />
                  <input value={iv.role} onChange={e => updateInterviewer(stepIdx, intIdx, "role", e.target.value)} placeholder={lang === "es" ? "Cargo" : "Role"} style={isMobile ? { ...inp, flex: "1 1 140px" } : { ...inp, flex: 1, padding: "7px 10px", fontSize: 12 }} />
                  <button onClick={() => removeInterviewer(stepIdx, intIdx)} aria-label={lang === "es" ? "Quitar entrevistador" : "Remove interviewer"} title={lang === "es" ? "Quitar entrevistador" : "Remove interviewer"} style={{ ...iconBtn, color: FAINT, ...(isMobile ? { width: 40, height: 40 } : {}) }}><X size={16} strokeWidth={1.75} /></button>
                </div>
              );
            })}
            <button onClick={() => addInterviewer(stepIdx)} style={{ ...btn.soft, ...btnSm, marginTop: 4, ...(isMobile ? { minHeight: 40 } : {}) }}>
              {lang === "es" ? "+ Añadir entrevistador" : "+ Add interviewer"}
            </button>
          </div>
        ))}

        {formErr && <div role="alert" style={{ ...errorBanner, marginBottom: 12 }}>{formErr}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? { flex: 1, minHeight: 44 } : {}) }}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
          <button onClick={handleSave} disabled={saving} style={{ ...(saving ? btn.disabled : btn.primary), ...(isMobile ? { flex: 1, minHeight: 44 } : {}) }}>
            {saving ? (lang === "es" ? "Guardando..." : "Saving...") : (lang === "es" ? "✓ Guardar" : "✓ Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ───────── Main IntakeFormView ─────────

const IntakeFormView = ({ token }) => {
  // Avisos en la página, junto al botón (24/10): antes eran alert() del navegador
  const [formErr, setFormErr] = useState("");
  const [lang, setLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState(null);
  const [cultureDoc, setCultureDoc] = useState(null);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [cvFile, setCvFile] = useState(null);

  const [personal, setPersonal] = useState({ name: "", email: "", phone: "", city: "", salary: "", notice: "", linkedin: "" });
  const [answers, setAnswers] = useState({});

  const [isAdmin, setIsAdmin] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  // Ambos plegados de inicio: el candidato ve la estructura de la posición de
  // un vistazo y abre lo que le interese, en vez de encontrarse un muro de texto.
  const [jdOpen, setJdOpen] = useState(false);
  const [cultureOpen, setCultureOpen] = useState(false);
  // La mayoría de candidatos abre este enlace en el móvil
  const isMobile = useIsMobile();

  useEffect(() => { loadIntake(); }, [token]);

  // Load DM Sans font (this view doesn't include the App.js link tag because it renders standalone)
  useEffect(() => {
    const id = "intake-dm-sans-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Bebas+Neue&family=JetBrains+Mono:wght@700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email && ADMIN_EMAILS.includes(data.user.email)) setIsAdmin(true);
    }).catch(() => {});
  }, []);

  const loadIntake = async () => {
    setLoading(true);
    try {
      // Una función comprueba el token y devuelve SOLO lo que este formulario
      // enseña. Antes se leían las tablas directamente, y para eso tenían que
      // estar abiertas a cualquier visitante (06/10/2026).
      const { data: res, error: rpcErr } = await supabase.rpc("get_intake_form", { p_token: token });
      if (rpcErr || !res || res.error === "invalid") { setError("invalid"); setLoading(false); return; }
      if (res.error) { setError("not-found"); setLoading(false); return; }

      const pos = res.position;
      setPosition(pos);
      // Idioma inicial: el de la posición si la función lo devuelve; si no, inglés
      const posLang = pos?.lang || pos?.language || res.lang || res.language;
      if (posLang === "es" || posLang === "en") setLang(posLang);
      if (res.culture_doc) setCultureDoc(res.culture_doc);

      if (pos.qual_questions) {
        const init = {};
        pos.qual_questions.forEach(s => {
          if (s.section !== "Personal data") {
            s.items.forEach(item => { init[item.n] = ""; });
          }
        });
        setAnswers(init);
      }
    } catch (e) {
      console.error(e);
      setError("generic");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setFormErr("");
    if (!policyAccepted) { setFormErr(lang === "es" ? "Acepta la política de privacidad antes de enviar." : "Please accept the privacy policy before submitting."); return; }
    if (!personal.name.trim() || !personal.email.trim()) { setFormErr(lang === "es" ? "El nombre y el email son obligatorios." : "Name and email are required."); return; }

    setSubmitting(true);
    try {
      // CV: el visitante no tiene permiso sobre el almacén. Pide a la ruta de
      // servidor un permiso de subida de un solo uso (valida el token de
      // intake) y sube con él a una ruta imposible de adivinar. En cv_url va
      // esa ruta; el bucket es privado y se firma al leerla.
      // Si el CV falla, se avisa y NO se envía: antes se perdía en silencio.
      let cvUrl = null;
      if (cvFile) {
        try {
          const r = await fetch("/api/intake-cv-upload", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, fileName: cvFile.name }),
          });
          const up = await r.json().catch(() => ({}));
          if (!r.ok || !up.path || !up.uploadToken) throw new Error(up.error || `HTTP ${r.status}`);
          const { error: upErr } = await supabase.storage.from("cvs").uploadToSignedUrl(up.path, up.uploadToken, cvFile, { contentType: cvFile.type || undefined });
          if (upErr) throw upErr;
          cvUrl = up.path;
        } catch (cvErr) {
          console.error("CV upload error:", cvErr);
          setFormErr(lang === "es"
            ? "No hemos podido subir tu CV (" + (cvErr.message || "error") + "). Inténtalo de nuevo, o quita el archivo y envía la candidatura sin él."
            : "We couldn't upload your CV (" + (cvErr.message || "error") + "). Please try again, or remove the file and send the application without it.");
          setSubmitting(false);
          return;
        }
      }

      const qualSections = position?.qual_questions ? position.qual_questions.map(section => ({
        section: section.section,
        items: section.items.map(item => {
          if (section.section === "Personal data") {
            const map = { "1.1": personal.name, "1.2": personal.city, "1.3": personal.salary, "1.4": personal.notice };
            return { n: item.n, q: item.q, a: map[item.n] || "" };
          }
          return { n: item.n, q: item.q, a: answers[item.n] || "" };
        }),
      })) : [];

      // Guarda una función que comprueba el token. Antes se escribía en
      // `candidates` desde el navegador: un visitante no tenía permiso, fallaba
      // en silencio y el formulario decía "enviado" sin haber guardado nada.
      // Ahora, si no se guarda, el candidato lo sabe.
      const { data: res, error: rpcErr } = await supabase.rpc("submit_intake", {
        p_token: token,
        p_data: {
          name: personal.name,
          email: personal.email.trim(),
          phone: personal.phone,
          city: personal.city,
          salary: personal.salary,
          notice: personal.notice,
          linkedin: personal.linkedin,
          qual_sections: qualSections,
          cv_url: cvUrl,
        },
      });
      if (rpcErr || !res?.ok) {
        console.error(rpcErr || res);
        setFormErr(lang === "es"
          ? "No hemos podido guardar tu candidatura. Inténtalo de nuevo o escribe a borja@rutment.com."
          : "We couldn't save your application. Please try again, or write to borja@rutment.com.");
        setSubmitting(false);
        return;
      }

      track("candidate_added", { position: position?.title || "", resubmission: !!res.resubmission });
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setFormErr(lang === "es" ? "Se ha producido un error al enviar. Inténtalo de nuevo." : "An error occurred while submitting. Please try again.");
    }
    setSubmitting(false);
  };

  // Móvil: 16px para que iOS no haga zoom al enfocar, y 44 de alto mínimo
  const inputStyle = isMobile
    ? { width: "100%", boxSizing: "border-box", padding: "11px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 16, minHeight: 44, outline: "none", fontFamily: "inherit", background: WHITE }
    : { width: "100%", boxSizing: "border-box", padding: "10px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", background: WHITE };
  const cardPad = isMobile ? "20px 16px" : "24px 28px";
  const pagePad = isMobile ? 16 : 24;
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: TEXT, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 };

  const processSteps = (position?.selection_process && position.selection_process.length)
    ? position.selection_process
    : defaultProcess(lang);

  const cleanedJD = stripJdHeader(position?.job_description);

  const handleProcessSave = (newSteps) => {
    setPosition(prev => ({ ...prev, selection_process: newSteps }));
    setEditorOpen(false);
  };

  if (loading) return <PageLoader minHeight="100vh" background={SURFACE} label={lang === "es" ? "Cargando..." : "Loading..."} />;

  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: SURFACE, fontFamily: "'DM Sans', sans-serif", padding: pagePad }}>
      <div style={{ background: WHITE, borderRadius: 16, padding: isMobile ? "32px 20px" : "40px 48px", maxWidth: 480, width: "100%", textAlign: "center", boxShadow: SHADOW_POP }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: SURFACE, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}><CircleAlert size={22} strokeWidth={1.75} color={MUTED} /></div>
        <div style={{ ...text.cardTitle, marginBottom: 8 }}>{lang === "es" ? "Enlace no disponible" : "Link not available"}</div>
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{(ERROR_TEXT[error] || ERROR_TEXT.generic)[lang]}</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: SURFACE, fontFamily: "'DM Sans', sans-serif", padding: pagePad }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ background: WHITE, borderRadius: 16, padding: isMobile ? "32px 20px" : "48px", maxWidth: 520, width: "100%", textAlign: "center", boxShadow: SHADOW_POP }}>
        <div style={{ width: 48, height: 48, background: SURFACE, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}><Check size={22} strokeWidth={1.75} color={TEXT} /></div>
        <div style={{ fontWeight: 800, fontSize: isMobile ? 22 : 22, color: INK, marginBottom: 12, letterSpacing: -0.5 }}>{lang === "es" ? "¡Candidatura recibida!" : "Application received!"}</div>
        <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.7, margin: "0 0 24px" }}>
          {lang === "es"
            ? <>Gracias, {personal.name.split(" ")[0]}. Hemos recibido tu candidatura para <strong>{position?.title}</strong>. Nuestro equipo revisará tu perfil y se pondrá en contacto contigo pronto.</>
            : <>Thank you, {personal.name.split(" ")[0]}. We have received your application for <strong>{position?.title}</strong>. Our team will review your profile and be in touch soon.</>}
        </p>
        <div style={{ background: SURFACE, borderRadius: 10, padding: "16px 20px", textAlign: "left", marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{lang === "es" ? "Próximos pasos" : "What happens next"}</div>
          {processSteps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: i < processSteps.length - 1 ? 12 : 0 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? BRAND : LINE, color: i === 0 ? WHITE : MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{s.n}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? INK : MUTED }}>{s.label}</div>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.4 }}>{renderStepDesc(s.desc, lang)}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: FAINT, margin: 0 }}>{lang === "es" ? "¿Dudas? Escríbenos a borja@rutment.com" : "Questions? Contact us at borja@rutment.com"}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: SURFACE, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } } * { box-sizing: border-box; }`}</style>

      {/* Header */}
      <div style={{ background: WHITE, borderBottom: `1.5px solid ${LINE}`, padding: isMobile ? "8px 16px" : "16px 32px", display: "flex", alignItems: "center", gap: 10 }}>
        <Logo size={22} />
        {!isMobile && <span style={{ fontSize: 11, color: FAINT, fontWeight: 500 }}>{lang === "es" ? "Candidatura" : "Candidate application"}</span>}
        {/* Selector de idioma: mismo que ReferenceFormView */}
        <div style={{ marginLeft: "auto", ...segWrap }}>
          {["es", "en"].map(l => (
            <button key={l} onClick={() => setLang(l)}
              style={{ ...segBtn(lang === l), padding: "5px 10px", fontSize: 11, textTransform: "uppercase", ...(isMobile ? { minWidth: 44, minHeight: 36, fontSize: 12 } : {}) }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "24px 16px calc(40px + env(safe-area-inset-bottom))" : "40px 24px 80px" }}>

        {step === 1 && (
          <div>
            <div style={{ marginBottom: isMobile ? 20 : 32 }}>
              <div style={{ ...text.label, marginBottom: 6 }}>{lang === "es" ? "Información relevante" : "Relevant information"}</div>
              <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: INK, margin: "0 0 4px", letterSpacing: -0.5 }}>{position?.title}</h1>
            </div>

            {/* Selection process */}
            <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, padding: cardPad, marginBottom: isMobile ? 16 : 24, position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>{lang === "es" ? "Proceso de selección" : "Selection process"}</div>
                {isAdmin && (
                  <button onClick={() => setEditorOpen(true)}
                    style={{ ...btn.soft, ...btnSm, ...(isMobile ? { minHeight: 40, padding: "5px 14px" } : {}) }}>
                    {lang === "es" ? "✎ Editar" : "✎ Edit"}
                  </button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 18 : 24 }}>
                {processSteps.map((s, i) => {
                  const hasInterviewers = (s.interviewers || []).length > 0;
                  const hasAddress = !!s.address;
                  // Móvil: número + texto; los entrevistadores bajan a una fila
                  // propia debajo del texto en vez de ocupar una columna de 280
                  return (
                    <div key={i} style={isMobile
                      ? { display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", columnGap: 12, rowGap: hasInterviewers ? 10 : 0, alignItems: "start" }
                      : { display: "grid", gridTemplateColumns: "32px 1fr 280px", gap: 14, alignItems: "center", minHeight: 64 }}>
                      {/* Step number */}
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: BRAND_LIGHT, color: BRAND, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>{s.n}</div>

                      {/* Label + description */}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{s.label}</div>
                        <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>{renderStepDesc(s.desc, lang)}</div>
                        {hasAddress && (
                          <div style={{ fontSize: 12, color: BRAND, fontWeight: 600, marginTop: 4 }}>{s.address}</div>
                        )}
                      </div>

                      {/* Right zone — always reserved, even if empty, to keep alignment */}
                      <div style={isMobile
                        ? { display: hasInterviewers ? "flex" : "none", gap: 12, justifyContent: "flex-start", flexWrap: "wrap", gridColumn: 2 }
                        : { display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        {hasInterviewers && s.interviewers.map((iv, j) => (
                          <div key={j} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 64 }}>
                            {iv.photo_url ? (
                              <img src={iv.photo_url} alt={iv.name} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: `2px solid ${BRAND_LIGHT}` }} />
                            ) : (
                              <div style={{ width: 44, height: 44, borderRadius: "50%", background: NEUTRAL_BG, color: TEXT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>
                                {(iv.name || "?").slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div style={{ fontSize: 11, fontWeight: 700, color: INK, marginTop: 4, textAlign: "center", lineHeight: 1.2 }}>{iv.name}</div>
                            {iv.role && <div style={{ fontSize: 11, color: MUTED, textAlign: "center", lineHeight: 1.2 }}>{iv.role}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Job description (collapsible, collapsed by default) */}
            {cleanedJD && (
              <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, marginBottom: isMobile ? 16 : 24, overflow: "hidden" }}>
                <button onClick={() => setJdOpen(v => !v)}
                  style={{ width: "100%", background: "none", border: "none", padding: isMobile ? "16px" : "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>{lang === "es" ? "Descripción del puesto" : "Job description"}</span>
                  <span style={{ fontSize: 16, color: MUTED, transform: jdOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", lineHeight: 1 }}>⌄</span>
                </button>
                {jdOpen && (
                  <div style={{ padding: isMobile ? "0 16px 20px" : "0 28px 24px", fontSize: isMobile ? 15 : 13, color: TEXT, lineHeight: isMobile ? 1.7 : 1.8, overflowWrap: isMobile ? "anywhere" : undefined }}>
                    {renderMarkdown(cleanedJD)}
                  </div>
                )}
              </div>
            )}

            {/* Culture doc (collapsible, collapsed by default) */}
            {cultureDoc && (
              <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, marginBottom: isMobile ? 16 : 24, overflow: "hidden" }}>
                <button onClick={() => setCultureOpen(v => !v)}
                  style={{ width: "100%", background: "none", border: "none", padding: isMobile ? "16px" : "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>{lang === "es" ? "Cultura de la empresa" : "Company culture"}</span>
                  <span style={{ fontSize: 16, color: MUTED, transform: cultureOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", lineHeight: 1 }}>⌄</span>
                </button>
                {cultureOpen && (
                  <div style={{ padding: isMobile ? "0 16px 20px" : "0 28px 24px", fontSize: isMobile ? 15 : 13, color: TEXT, lineHeight: isMobile ? 1.7 : 1.8, overflowWrap: isMobile ? "anywhere" : undefined }}>
                    {renderMarkdown(cultureDoc)}
                  </div>
                )}
              </div>
            )}

            <button onClick={() => { setStep(2); if (isMobile) window.scrollTo(0, 0); }}
              style={{ ...btn.primary, padding: isMobile ? "14px 20px" : "14px 36px", fontSize: 15, width: "100%" }}>
              {lang === "es" ? "Presentar mi candidatura →" : "Apply for this position →"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <BackButton onClick={() => { setStep(1); if (isMobile) window.scrollTo(0, 0); }} label={lang === "es" ? "Volver a la posición" : "Back to position info"} ariaLabel={lang === "es" ? "Volver a la posición" : "Back to position info"} style={{ display: "flex", marginBottom: isMobile ? 12 : 24 }} />

            <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: INK, margin: "0 0 6px", letterSpacing: -0.5 }}>{lang === "es" ? "Tu candidatura" : "Your application"}</h1>
            <p style={{ fontSize: 13, color: MUTED, margin: isMobile ? "0 0 20px" : "0 0 32px", lineHeight: 1.6 }}>{lang === "es" ? "Responde a todas las preguntas con el mayor detalle posible. Nuestro equipo revisará tus respuestas." : "Please answer all questions as thoroughly as possible. Your responses will be reviewed by our team."}</p>

            <div style={{ background: WHITE, border: "1.5px solid " + LINE, borderRadius: 14, padding: isMobile ? "18px 16px" : "20px 24px", marginBottom: isMobile ? 16 : 32 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: INK, marginBottom: 8 }}>{lang === "es" ? "Política de privacidad" : "Privacy policy"}</div>
              <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.7, margin: "0 0 12px" }}>
                {lang === "es"
                  ? "Los datos que facilites en este formulario se tratarán exclusivamente para evaluar tu candidatura. Conforme al RGPD, garantizamos la confidencialidad y la seguridad de la información."
                  : "Data provided in this form will be processed exclusively to evaluate your candidacy. In accordance with GDPR, we guarantee the confidentiality and security of the information."}
              </p>
              <button onClick={() => setShowPolicy(v => !v)} style={{ ...linkBtn, padding: "0 0 12px", ...(isMobile ? { fontSize: 13, minHeight: 40, padding: "0 0 4px" } : {}) }}>
                {showPolicy
                  ? (lang === "es" ? "Ocultar política completa ▲" : "Hide full policy ▲")
                  : (lang === "es" ? "Leer política completa ▼" : "Read full policy ▼")}
              </button>
              {showPolicy && (
                <div style={{ background: SURFACE, borderRadius: 10, padding: "16px 18px", marginBottom: 14, maxHeight: 260, overflowY: "auto" }}>
                  <pre style={{ fontSize: 12, color: TEXT, lineHeight: 1.8, margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{lang === "es" ? PRIVACY_POLICY_ES : PRIVACY_POLICY}</pre>
                </div>
              )}
              <label style={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 10, cursor: "pointer", ...(isMobile ? { padding: "8px 0" } : {}) }}>
                <input type="checkbox" checked={policyAccepted} onChange={e => setPolicyAccepted(e.target.checked)}
                  style={{ marginTop: 2, width: isMobile ? 22 : 16, height: isMobile ? 22 : 16, accentColor: BRAND, cursor: "pointer", flexShrink: 0 }} />
                <span style={{ fontSize: isMobile ? 15 : 13, color: INK, lineHeight: 1.5 }}>
                  {lang === "es" ? "He leído y acepto la política de privacidad y protección de datos." : "I have read and accept the privacy and data protection policy."} <span style={{ color: BRAND }}>*</span>
                </span>
              </label>
            </div>

            <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, padding: cardPad, marginBottom: isMobile ? 16 : 24 }}>
              <div style={{ ...text.label, marginBottom: isMobile ? 16 : 20 }}>{lang === "es" ? "Datos personales" : "Personal data"}</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "1fr 1fr", gap: 16 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{lang === "es" ? "Nombre completo" : "Full name"} <span style={{ color: BRAND }}>*</span></label>
                  <input value={personal.name} onChange={e => setPersonal(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder={lang === "es" ? "Tu nombre completo" : "Your full name"}
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div>
                  <label style={labelStyle}>Email <span style={{ color: BRAND }}>*</span></label>
                  <input type="email" autoCapitalize={isMobile ? "none" : undefined} value={personal.email} onChange={e => setPersonal(p => ({ ...p, email: e.target.value }))} style={inputStyle} placeholder={lang === "es" ? "tu@email.com" : "your@email.com"}
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div>
                  <label style={labelStyle}>{lang === "es" ? "Teléfono" : "Phone"}</label>
                  <input inputMode={isMobile ? "tel" : undefined} value={personal.phone} onChange={e => setPersonal(p => ({ ...p, phone: e.target.value }))} style={inputStyle} placeholder="+34 600 000 000"
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div>
                  <label style={labelStyle}>{lang === "es" ? "Ciudad" : "City"} <span style={{ color: BRAND }}>*</span></label>
                  <input value={personal.city} onChange={e => setPersonal(p => ({ ...p, city: e.target.value }))} style={inputStyle} placeholder="Madrid"
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div>
                  <label style={labelStyle}>{lang === "es" ? "Salario actual / expectativa" : "Current salary / expectation"} <span style={{ color: BRAND }}>*</span></label>
                  <input value={personal.salary} onChange={e => setPersonal(p => ({ ...p, salary: e.target.value }))} style={inputStyle} placeholder={lang === "es" ? "p. ej. 80.000€ / 90.000€" : "e.g. 80.000€ / 90.000€"}
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div>
                  <label style={labelStyle}>{lang === "es" ? "Preaviso" : "Notice period"} <span style={{ color: BRAND }}>*</span></label>
                  <input value={personal.notice} onChange={e => setPersonal(p => ({ ...p, notice: e.target.value }))} style={inputStyle} placeholder={lang === "es" ? "p. ej. 1 mes" : "e.g. 1 month"}
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{lang === "es" ? "URL de tu perfil de LinkedIn" : "LinkedIn profile URL"}</label>
                  <input inputMode={isMobile ? "url" : undefined} autoCapitalize={isMobile ? "none" : undefined} value={personal.linkedin} onChange={e => setPersonal(p => ({ ...p, linkedin: e.target.value }))} style={inputStyle} placeholder={lang === "es" ? "https://linkedin.com/in/tuperfil" : "https://linkedin.com/in/yourprofile"}
                    onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{lang === "es" ? "CV (PDF)" : "CV / Resume (PDF)"}</label>
                  <div style={{ border: `1.5px dashed ${LINE}`, borderRadius: 8, padding: isMobile ? 0 : "16px", textAlign: "center", background: SURFACE }}>
                    {cvFile ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, ...(isMobile ? { padding: "6px 6px 6px 14px", justifyContent: "space-between", minHeight: 52 } : {}) }}>
                        <span style={{ fontSize: 13, color: INK, fontWeight: 600, ...(isMobile ? { fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" } : {}) }}>📄 {cvFile.name}</span>
                        <button onClick={() => setCvFile(null)} style={{ ...linkBtn, color: MUTED, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, ...(isMobile ? { fontSize: 13, minHeight: 40, padding: "0 10px", flexShrink: 0 } : {}) }}><X size={14} strokeWidth={1.75} />{lang === "es" ? "Quitar" : "Remove"}</button>
                      </div>
                    ) : isMobile ? (
                      // Móvil: no se arrastra; toda la caja es el botón de elegir archivo
                      <label style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 56, padding: "12px 16px" }}>
                        <span style={{ color: BRAND, fontWeight: 700, fontSize: 15 }}>{lang === "es" ? "Subir CV" : "Upload CV"}</span>
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCvFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
                      </label>
                    ) : (
                      <label style={{ cursor: "pointer" }}>
                        <span style={{ fontSize: 13, color: MUTED }}>{lang === "es" ? "Arrastra aquí tu CV o " : "Drop your CV here or "}</span>
                        <span style={{ color: BRAND, fontWeight: 700, fontSize: 13 }}>{lang === "es" ? "búscalo" : "browse"}</span>
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCvFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {position?.qual_questions?.filter(s => s.section !== "Personal data").map((section, si) => (
              <div key={si} style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, padding: cardPad, marginBottom: isMobile ? 16 : 24 }}>
                <div style={{ ...text.label, marginBottom: isMobile ? 16 : 20 }}>{section.section}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {section.items.map(item => (
                    <div key={item.n}>
                      <label style={{ display: "block", fontSize: isMobile ? 15 : 13, fontWeight: 600, color: INK, lineHeight: 1.5, marginBottom: 8 }}>
                        <span style={{ color: FAINT, fontWeight: 700, marginRight: 8, fontSize: 11 }}>{item.n}</span>
                        {item.q} <span style={{ color: BRAND }}>*</span>
                      </label>
                      <textarea
                        value={answers[item.n] || ""}
                        onChange={e => setAnswers(prev => ({ ...prev, [item.n]: e.target.value }))}
                        rows={4}
                        style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                        onFocus={e => e.target.style.borderColor = BRAND}
                        onBlur={e => e.target.style.borderColor = LINE}
                        placeholder={lang === "es" ? "Tu respuesta" : "Your answer"}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ marginTop: 8 }}>
              {!policyAccepted && (
                <p style={{ background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: TEXT, lineHeight: 1.6, marginBottom: 16 }}>
                  {lang === "es" ? "Acepta la política de privacidad antes de enviar." : "Please accept the privacy policy before submitting."}
                </p>
              )}
              {formErr && <div role="alert" style={{ ...errorBanner, marginBottom: 12 }}>{formErr}</div>}
              <button onClick={handleSubmit} disabled={submitting || !policyAccepted}
                style={{ ...(submitting || !policyAccepted ? btn.disabled : btn.primary), padding: "14px 36px", fontSize: 15, width: "100%", transition: "background 0.2s" }}>
                {submitting ? (lang === "es" ? "Enviando..." : "Submitting...") : (lang === "es" ? "Enviar candidatura →" : "Submit application →")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selection Process Editor (admin only) */}
      {editorOpen && isAdmin && position && (
        <SelectionProcessEditor
          steps={processSteps}
          positionId={position.id}
          onSave={handleProcessSave}
          onClose={() => setEditorOpen(false)}
          lang={lang}
        />
      )}
    </div>
  );
};

export default IntakeFormView;
